<template>
  <div>
    <div class="card bg-primary text-white mb-4">
      <h5 class="card-header mb-0">{{ $t('Webportal.Actions.InquiryWizard.Step3.Title') }}</h5>
    </div>

    <!-- Info Box: Nur Bewertungen mit Optimierungspotenzial -->
    <b-card header-bg-variant="warning" class="mb-4">
      <template #header>
        <div class="d-flex align-items-center">
          <font-awesome-icon :icon="['fal', 'info-circle']" class="mr-2" />
          <h6 class="mb-0 font-weight-bold">{{ $t('Webportal.Actions.InquiryWizard.Step3.InfoTitle') }}</h6>
        </div>
      </template>
      <p class="mb-0 small">
        {{ $t('Webportal.Actions.InquiryWizard.Step3.InfoDescription') }}
      </p>
    </b-card>

    <!-- Question Count Card -->
    <b-card class="mb-4">
      <div class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <div class="question-count-rounded-circle d-flex align-items-center justify-content-center mr-3">
            <strong>{{ selectedQuestionCount }}</strong>
          </div>
          <div>
            <div class="font-weight-bold">{{ $t('Webportal.Actions.InquiryWizard.Step3.SelectedCount', { count: selectedQuestionCount }) }}</div>
            <div class="text-muted small">{{ $t('Webportal.Actions.InquiryWizard.Step3.MinRequired') }}</div>
          </div>
        </div>
        <div v-if="selectedQuestionCount > 0">
          <a href="#" @click.prevent="deselectAllQuestions" class="text-danger">{{ $t('Webportal.Actions.InquiryWizard.Step3.DeselectAll') }}</a>
        </div>
      </div>
    </b-card>

    <!-- Loading State -->
    <div v-if="isLoadingCheckData" class="text-center my-5">
      <b-spinner variant="primary" :label="$t('Webportal.Loading')"></b-spinner>
      <p class="mt-3 text-muted">{{ $t('Webportal.Loading') }}</p>
    </div>

    <!-- Main Content: Sidebar + Questions -->
    <div v-else-if="checkData && complainableQuestions.length > 0" class="row">
      <!-- Left Sidebar: Categories -->
      <div class="col-lg-3 col-md-5 mb-4 pr-0">
        <div class="sticky-sidebar">
          <b-card>
            <h6 class="font-weight-bold mb-3">{{ $t('Webportal.Categories') }}</h6>
            <div class="cluster-list">
              <a href="#" @click.prevent="selectCluster(null)" class="cluster-item" :class="{ active: selectedCluster === null }">
                <div class="d-flex align-items-center justify-content-between small">
                  <span>{{ $t('Webportal.Actions.InquiryWizard.Step3.AllQuestions') }}</span>
                  <span class="text-muted">{{ complainableQuestions.length }}</span>
                </div>
              </a>
              <a
                v-for="cluster in clusters"
                :key="cluster.id"
                href="#"
                @click.prevent="selectCluster(cluster.id)"
                class="cluster-item"
                :class="{ active: selectedCluster === cluster.id }"
              >
                <div class="d-flex align-items-center justify-content-between small">
                  <span>{{ cluster.name }}</span>
                  <span class="text-muted">{{ cluster.questionCount }}</span>
                </div>
                <div v-if="clusterSelectedCounts[cluster.id] > 0" class="text-success small">
                  {{ $t('Webportal.Actions.InquiryWizard.Step3.ClusterSelected', { count: clusterSelectedCounts[cluster.id] }) }}
                </div>
              </a>
            </div>
          </b-card>
        </div>
      </div>

      <!-- Right Content: Questions -->
      <div class="col-lg-9 col-md-7">
        <!-- Cluster Groups -->
        <div v-for="(group, groupIndex) in groupedQuestions" :key="groupIndex">
          <!-- Cluster Header -->
          <b-card header-bg-variant="light" class="mb-3" no-body>
            <template #header>
              <div class="d-flex align-items-center justify-content-between">
                <h5 class="mb-0">{{ group.clusterName }}</h5>
                <a href="#" @click.prevent="toggleQuestionSelectionInGroup(group)" class="text-primary">
                  {{ areAllQuestionsInGroupSelected(group) ? $t('Webportal.Actions.InquiryWizard.Step3.DeselectAll') : $t('Webportal.Actions.InquiryWizard.Step3.SelectAll') }}
                </a>
              </div>
            </template>
          </b-card>

          <!-- Questions List -->
          <div v-for="question in group.questions" :key="question.SurveyQuestionId">
            <b-card
              class="question-card mb-3"
              :class="{ selected: isQuestionSelected(question.SurveyQuestionId) }"
              @click="toggleQuestion(question.SurveyQuestionId)"
            >
              <div class="d-flex">
                <b-form-checkbox
                  :checked="isQuestionSelected(question.SurveyQuestionId)"
                  @click.stop
                  @change="toggleQuestion(question.SurveyQuestionId)"
                  class="question-checkbox mr-3"
                  size="lg"
                />
                <div class="flex-grow-1">
                  <!-- Question Header -->
                  <div class="d-flex align-items-start mb-2">
                    <div class="d-flex align-items-center mr-3">
                      <span class="font-weight-bold mr-2">{{ question.QuestionNumber }}</span>
                      <b-badge variant="light">{{ question.ClusterText }}</b-badge>
                    </div>
                  </div>

                  <!-- Question Text -->
                  <p class="mb-3">{{ question.QuestionText }}</p>

                  <!-- Erwartung laut Leitfaden -->
                  <CollapsibleInfo
                    v-if="question.GuidelineTitle || question.GuidelineDescription"
                    :title="$t('Webportal.Actions.InquiryWizard.GuidelineExpectation')"
                    :sub-title="question.GuidelineTitle"
                    :description="question.GuidelineDescription"
                    variant="info"
                    :show-toggle="true"
                    class="mb-3"
                  />

                  <!-- Antwort aus dem Erlebnisbericht -->
                  <QuestionResult
                    :result="question.Result"
                    :selected-answers="question.SelectedAnswers"
                    :comment="question.CommentByShopper"
                  />

                  <!-- Question Type -->
                  <div class="mt-3">
                    <font-awesome-icon :icon="['fal', 'list']" class="text-muted mr-2" />
                    <span class="small text-muted">{{ $t('Webportal.Actions.InquiryWizard.Step3.QuestionType') }}: {{ getQuestionType(question) }}</span>
                  </div>
                </div>
              </div>
            </b-card>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center my-5">
      <font-awesome-icon :icon="['fal', 'info-circle']" size="3x" class="text-muted mb-3" />
      <p class="text-muted">{{ $t('Webportal.Actions.InquiryWizard.Step3.NoQuestions') }}</p>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ActionService from "@/services/ActionService";
import ScaleIndicator from "./ScaleIndicator.vue";
import QuestionResult from "./QuestionResult.vue";
import CollapsibleInfo from "./CollapsibleInfo.vue";

export default {
  name: "Step3SelectQuestions",

  components: {
    ScaleIndicator,
    QuestionResult,
    CollapsibleInfo,
  },

  data() {
    return {
      isLoadingCheckData: false,
      checkData: null,
      selectedCluster: null,
    };
  },

  computed: {
    ...mapGetters("inquiryWizard", ["selectedCheckId", "selectedQuestionIds", "selectedQuestionCount"]),

    complainableQuestions() {
      if (!this.checkData || !this.checkData.Questions) {
        return [];
      }

      // Map comments, guidelines, and answers to questions
      const questionsWithComments = this.checkData.Questions.map((question) => {
        // Find comment for this question by checking if comment's QuestionNumber
        // starts with the question's QuestionNumber when IsCommentPossible is false
        // Example: question "G11." should match comment "G11. cc"
        const comment = this.checkData.Comments?.find((c) =>
          question.IsCommentPossible
            ? c.QuestionNumber == question.QuestionNumber
            : question.QuestionNumber && c.QuestionNumber.startsWith(question.QuestionNumber)
        );

        // Find guideline for this question by SurveyQuestionId
        const guideline = this.checkData.Guidelines?.find((g) => g.SurveyQuestionId === question.SurveyQuestionId);

        // Find all answers for this question by SurveyQuestionId, filter to only show selected answers
        const answers =
          this.checkData.Answers?.filter((a) => a.SurveyQuestionId === question.SurveyQuestionId && a.IsAnswerSelected === true) || [];

        return {
          ...question,
          CommentByShopper: comment ? comment.CommentByShopper : null,
          GuidelineTitle: guideline ? guideline.GuidelineTitle : null,
          GuidelineDescription: guideline ? guideline.GuidelineDescription : null,
          SelectedAnswers: answers,
        };
      });

      return questionsWithComments.filter((q) => q.IsComplainable);
    },

    clusters() {
      const clusterMap = new Map();

      this.complainableQuestions.forEach((question) => {
        const clusterName = question.ClusterText;
        if (clusterName) {
          if (!clusterMap.has(clusterName)) {
            clusterMap.set(clusterName, {
              id: clusterName,
              name: clusterName,
              questionCount: 0,
            });
          }
          clusterMap.get(clusterName).questionCount++;
        }
      });

      return Array.from(clusterMap.values());
    },

    clusterSelectedCounts() {
      const counts = {};
      this.complainableQuestions.forEach((question) => {
        const clusterName = question.ClusterText;
        if (clusterName) {
          if (!counts[clusterName]) {
            counts[clusterName] = 0;
          }
          if (this.selectedQuestionIds.includes(question.SurveyQuestionId)) {
            counts[clusterName]++;
          }
        }
      });
      return counts;
    },

    selectedClusterName() {
      if (!this.selectedCluster) return null;
      const cluster = this.clusters.find((c) => c.id === this.selectedCluster);
      return cluster ? cluster.name : null;
    },

    filteredQuestions() {
      if (!this.selectedCluster) {
        return this.complainableQuestions;
      }
      return this.complainableQuestions.filter((q) => q.ClusterText === this.selectedCluster);
    },

    groupedQuestions() {
      const groups = [];
      let currentGroup = null;

      this.filteredQuestions.forEach((question) => {
        const clusterName = question.ClusterText || this.$t('Webportal.NotAvailable');

        if (!currentGroup || currentGroup.clusterName !== clusterName) {
          currentGroup = {
            clusterName: clusterName,
            questions: [],
          };
          groups.push(currentGroup);
        }

        currentGroup.questions.push(question);
      });

      return groups;
    },
  },

  methods: {
    ...mapActions("inquiryWizard", ["toggleQuestionSelection", "setSelectedQuestionIds"]),

    async loadCheckData() {
      if (!this.selectedCheckId) {
        // Redirect to landing page when no check is selected
        this.$router.push("/action/inquiry-wizard");
        return;
      }

      this.isLoadingCheckData = true;
      try {
        const response = await ActionService.getSurveyInstanceData(this.selectedCheckId, true, true, true);
        this.checkData = response.data;
      } catch (error) {
        console.error("Error loading check data:", error);
        this.$bvToast.toast(this.$t('Webportal.Actions.InquiryWizard.Step3.LoadError'), {
          title: this.$t('Webportal.Error'),
          variant: "danger",
        });
      } finally {
        this.isLoadingCheckData = false;
      }
    },

    selectCluster(clusterText) {
      this.selectedCluster = clusterText;
    },

    toggleQuestion(questionId) {
      this.toggleQuestionSelection(questionId);
    },

    isQuestionSelected(questionId) {
      return this.selectedQuestionIds.includes(questionId);
    },

    toggleQuestionSelectionInGroup(group) {
      const questionIds = group.questions.map((q) => q.SurveyQuestionId);
      const allSelected = questionIds.every((id) => this.selectedQuestionIds.includes(id));

      if (allSelected) {
        // Deselect all in group
        const newSelection = this.selectedQuestionIds.filter((id) => !questionIds.includes(id));
        this.setSelectedQuestionIds(newSelection);
      } else {
        // Select all in group
        const newSelection = [...new Set([...this.selectedQuestionIds, ...questionIds])];
        this.setSelectedQuestionIds(newSelection);
      }
    },

    areAllQuestionsInGroupSelected(group) {
      const questionIds = group.questions.map((q) => q.SurveyQuestionId);
      return questionIds.length > 0 && questionIds.every((id) => this.selectedQuestionIds.includes(id));
    },

    deselectAllQuestions() {
      this.setSelectedQuestionIds([]);
    },

    getQuestionType(question) {
      if (question.IsCommentPossible) {
        return this.$t('Webportal.Actions.InquiryWizard.QuestionType.Comment');
      }
      if (question.IsMultipleChoice) {
        return this.$t('Webportal.Actions.InquiryWizard.QuestionType.MultipleChoice');
      }
      return this.$t('Webportal.Actions.InquiryWizard.QuestionType.SingleChoice');
    },
  },

  created() {
    // Load check data fresh
    this.loadCheckData();
  },
};
</script>

<style lang="scss" scoped>
.question-count-rounded-circle {
  border-radius: 50%;
  font-size: 1.25rem;
  width: 48px;
  height: 48px;
  background-color: var(--primary);
  color: var(--white);
}

.sticky-sidebar {
  position: sticky;
  top: 60px;
}

.cluster-list {
  display: flex;
  flex-direction: column;
}

.cluster-item {
  display: block;
  padding: 0.75rem 1rem;
  margin-bottom: 0.5rem;
  text-decoration: none;
  border-radius: 0.25rem;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
  }

  &.active {
    background-color: var(--primary);
    color: white;
    font-weight: 600;
  }
}

.question-card {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;

  &:hover {
    border: 2px solid var(--primary-20);
    transform: translateX(2px);
  }

  &.selected {
    border: 2px solid var(--primary);
  }
}

.question-checkbox {
  margin-top: -5px;
}
</style>
